/**
 * /start/shipping — funnel step 4 (Shipping address, ClickUp 86baetyga).
 *
 * Always shown between Product Selection and Payment. The address is REQUIRED
 * when the cart contains a physical item (a plan that includes the orb, or the
 * physical-orb add-on) and OPTIONAL otherwise — we still offer to collect it
 * for any physical extras (welcome packet, setup instructions). The selection
 * is read from sessionStorage (like the other steps); the address is handed to
 * /start/payment the same way. The server re-checks required-when-physical at
 * checkout.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { useAddons, useOfferings } from '@/hooks/useFunnel';
import {
  readFunnelSelection,
  readShippingAddress,
  storeShippingAddress,
  clearShippingAddress,
  DEFAULT_COUNTRY,
  US_STATES,
  US_STATE_CODES,
  type ShippingAddress,
} from '@/lib/funnel';

const EMPTY: ShippingAddress = {
  recipientName: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  country: DEFAULT_COUNTRY,
  phone: '',
};

/** US ZIP codes are exactly 5 digits (we ship US-only for now). */
const ZIP_REGEX = /^\d{5}$/;

export function ShippingPage() {
  const navigate = useNavigate();

  // Read once on mount — payment self-guards on a missing selection too.
  const [selection] = useState(readFunnelSelection);

  const {
    data: offerings,
    isLoading: offeringsLoading,
    isError: offeringsError,
    refetch: refetchOfferings,
  } = useOfferings();
  const {
    data: addons,
    isLoading: addonsLoading,
    isError: addonsError,
    refetch: refetchAddons,
  } = useAddons(selection?.offeringId);

  // Country is fixed to the US for now, so normalize any older stored address.
  const [form, setForm] = useState<ShippingAddress>(() => {
    const stored = readShippingAddress();
    return stored ? { ...stored, country: DEFAULT_COUNTRY } : EMPTY;
  });
  const [error, setError] = useState('');

  if (!selection) {
    return <Navigate to="/start/products" replace />;
  }

  const offering = offerings?.find((o) => o.id === selection.offeringId) ?? null;
  const chosenAddons = (addons ?? []).filter((a) => selection.addonIds.includes(a.id));

  // Add-ons only matter for the physical check when some were selected.
  const needsAddons = selection.addonIds.length > 0;
  const isLoading = offeringsLoading || (needsAddons && addonsLoading);
  const isFetchError = offeringsError || (needsAddons && addonsError);

  // The selected plan disappeared from the catalog (deactivated mid-funnel).
  if (!isLoading && offerings && !offering) {
    return <Navigate to="/start/products" replace />;
  }

  // A physical item means the address is mandatory (can't ship an orb nowhere).
  const requiresShipping =
    !!offering?.includesOrb || chosenAddons.some((a) => a.slug === 'physical-orb');

  const set = (key: keyof ShippingAddress) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // State is a closed dropdown (US-only); region holds the 2-letter USPS code.
  const setRegion = (region: string) => setForm((prev) => ({ ...prev, region }));

  // Postal code: keep digits only, capped at 5 (US ZIP).
  const setPostalCode = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, postalCode: e.target.value.replace(/\D/g, '').slice(0, 5) }));

  const trimmed = (): ShippingAddress => ({
    recipientName: form.recipientName.trim(),
    line1: form.line1.trim(),
    line2: form.line2?.trim() || undefined,
    city: form.city.trim(),
    region: form.region.trim(),
    postalCode: form.postalCode.trim(),
    country: form.country.trim(),
    phone: form.phone?.trim() || undefined,
  });

  const coreFilled = (a: ShippingAddress) =>
    a.recipientName && a.line1 && a.city && a.region && a.postalCode && a.country;

  const anyFilled = (a: ShippingAddress) =>
    a.recipientName || a.line1 || a.line2 || a.city || a.region || a.postalCode || a.phone;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const address = trimmed();

    // Optional path: a blank form means "no shipping address" — store nothing
    // and continue (mailing the physical extras is best-effort).
    if (!requiresShipping && !anyFilled(address)) {
      clearShippingAddress();
      navigate('/start/payment');
      return;
    }

    if (!coreFilled(address)) {
      setError(
        'Please complete the name, street address, city, state/region, postal code, and country.'
      );
      return;
    }

    if (!US_STATE_CODES.has(address.region)) {
      setError('Please choose a state from the list.');
      return;
    }

    if (!ZIP_REGEX.test(address.postalCode)) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }

    setError('');
    storeShippingAddress(address);
    navigate('/start/payment');
  };

  const handleSkip = () => {
    clearShippingAddress();
    navigate('/start/payment');
  };

  return (
    <FunnelLayout step={4} width="2xl">
      <div className="mb-6 text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Shipping address
        </h1>
        <p className="mt-2 text-muted-foreground">
          {requiresShipping
            ? 'Where should we ship your physical Legacy Orb?'
            : 'Please provide the address where you’d like us to send any physical items included with your order, such as your Legacy Orb, printed Legacy Book, welcome packet, and setup instructions.'}
        </p>
        {!requiresShipping && (
          <p className="mt-1 text-muted-foreground">
            If your plan does not include physical items, this step is optional.
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isFetchError ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center gap-3 rounded-md border border-border py-10 text-center">
            <p className="text-sm text-destructive">
              We could not load your order details. Please try again.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                if (offeringsError) void refetchOfferings();
                if (addonsError) void refetchAddons();
              }}
            >
              Try Again
            </Button>
          </div>
          <Button variant="outline" size="lg" onClick={() => navigate('/start/products')}>
            Back to Product Selection
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-2">
                <Label htmlFor="recipientName">Full name{requiresShipping ? ' *' : ''}</Label>
                <Input
                  id="recipientName"
                  value={form.recipientName}
                  onChange={set('recipientName')}
                  autoComplete="name"
                  placeholder="Jane Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="line1">Street address{requiresShipping ? ' *' : ''}</Label>
                <Input
                  id="line1"
                  value={form.line1}
                  onChange={set('line1')}
                  autoComplete="address-line1"
                  placeholder="123 Main St"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="line2">Apartment, suite, etc. (optional)</Label>
                <Input
                  id="line2"
                  value={form.line2 ?? ''}
                  onChange={set('line2')}
                  autoComplete="address-line2"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="city">City{requiresShipping ? ' *' : ''}</Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={set('city')}
                    autoComplete="address-level2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">State{requiresShipping ? ' *' : ''}</Label>
                  <Select value={form.region} onValueChange={setRegion}>
                    <SelectTrigger id="region">
                      <SelectValue placeholder="Select a state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="postalCode">ZIP code{requiresShipping ? ' *' : ''}</Label>
                  <Input
                    id="postalCode"
                    value={form.postalCode}
                    onChange={setPostalCode}
                    inputMode="numeric"
                    maxLength={5}
                    autoComplete="postal-code"
                    placeholder="12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country{requiresShipping ? ' *' : ''}</Label>
                  <Input
                    id="country"
                    value={form.country}
                    readOnly
                    autoComplete="country-name"
                    className="cursor-not-allowed bg-muted text-muted-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone ?? ''}
                  onChange={set('phone')}
                  autoComplete="tel"
                  placeholder="For delivery questions only"
                />
              </div>
            </CardContent>
          </Card>

          {error && <p className="text-center text-sm text-destructive">{error}</p>}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => navigate('/start/products')}
            >
              Back to Product Selection
            </Button>
            <div className="flex flex-col gap-3 sm:flex-row">
              {!requiresShipping && (
                <Button type="button" variant="ghost" size="lg" onClick={handleSkip}>
                  Skip for now
                </Button>
              )}
              <Button type="submit" size="lg">
                Continue to Payment
              </Button>
            </div>
          </div>
        </form>
      )}
    </FunnelLayout>
  );
}
